import React, { Fragment, useEffect } from "react";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import NotFound from "../screens/NotFound.js";
import routeMap from "./routes.js";
import useUserAgent from "../hooks/useUserAgent.js";
import { checkAppVersion, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import LoadingStatus from "../components/elements/LoadingStatus.js";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useGlobalContext from "../hooks/useGlobalContext.js";
import "./global.css";
import BoundsRedirect from "./BoundsRedirect.js";
import { isForceReloadOnNavigationNeeded } from "./force-window-reload.js";
import { A11Y_MOBILE_QUERY_PARAMS } from "@flights/constants";
import { clientFetch } from "@flights/client-fetch";
const Renderer = ({ component, path }) => {
    const forceReload = isFeatureRunningClientSide("FLIGHTS_FETA_REPLACE_APP_VERSION_MISMATCH_RELOAD") && isForceReloadOnNavigationNeeded();
    const isAppVersionMismatch = !isFeatureRunningClientSide("FLIGHTS_FETA_REPLACE_APP_VERSION_MISMATCH_RELOAD") &&
        checkAppVersion.isAppVersionMismatch();
    if (forceReload || isAppVersionMismatch) {
        void clientFetch("/events/squeak", {
            method: "post",
            body: JSON.stringify({
                message: `Window reload - reason ${isAppVersionMismatch ? "mismatch" : "reload"} - route ${path || "not defined"}`
            }),
            headers: {
                "Content-Type": "application/json",
                "X-Booking-Referrer": location.href || ""
            }
        }).finally(() => window.location.reload());
    }
    return component;
};
const Layout = ({ stateMap }) => {
    const { isMobile, isWebview } = useUserAgent();
    const { isPaymentCollectionPage, isNewFlightDetailsPage, isNewFlightDetailsDesktopPage } = useGlobalContext();
    const routes = routeMap({
        isMobile,
        isPaymentCollectionPage,
        isWebview,
        isNewFlightDetailsPage: !!isNewFlightDetailsPage,
        isNewFlightDetailsDesktopPage: !!isNewFlightDetailsDesktopPage,
        stateMap
    }).filter((route) => !route.exclude);
    const i18n = useI18n();
    const NavigationMiddleware = () => {
        const history = useHistory();
        const location = useLocation();
        useEffect(() => {
            const preserveA11yMobileView = () => {
                const currentParams = new URLSearchParams(location.search);
                const a11yMobileView = currentParams.get(A11Y_MOBILE_QUERY_PARAMS);
                if (a11yMobileView === "1") {
                    const unlisten = history.listen((newLocation) => {
                        const newParams = new URLSearchParams(newLocation.search);
                        if (!newParams.has(A11Y_MOBILE_QUERY_PARAMS)) {
                            newParams.set(A11Y_MOBILE_QUERY_PARAMS, "1");
                            history.replace({
                                ...newLocation,
                                search: newParams.toString()
                            });
                        }
                    });
                    return unlisten;
                }
                return () => { };
            };
            const unlisten = preserveA11yMobileView();
            return () => unlisten();
        }, [history, location]);
        return null;
    };
    return (React.createElement(Fragment, null,
        React.createElement(Helmet, null,
            React.createElement("title", null, i18n.trans(t("flights_page_title_bp_generic"))),
            React.createElement("meta", { property: "webview:header", content: "regular" }),
            React.createElement("meta", { property: "webview:title", content: "" }),
            isWebview && (React.createElement("meta", { name: "viewport", content: "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1" }))),
        React.createElement(NavigationMiddleware, null),
        React.createElement(Switch, null,
            routes.map((route) => {
                const { component: Component, ...otherProps } = route;
                return (React.createElement(Route, { key: route.path, ...otherProps },
                    React.createElement(BoundsRedirect, { route: route },
                        React.createElement(Renderer, { path: route.path, component: React.createElement(Component, { fallback: React.createElement(LoadingStatus, null), getInitialState: route.getInitialState }) }))));
            }),
            React.createElement(Route, null,
                React.createElement(Renderer, { component: React.createElement(NotFound, null) })))));
};
export default Layout;
